// Shared audio playback for short, one-shot TTS clips.
//
// Used by:
//   - AgentEditorModal voice-preview button (`/api/voice/preview`)
//   - AdminProdagiOnboardingModal voice-preview button
//     (`/api/admin/prodagi-agents/voice/preview`)
//   - Widget.tsx + VoicePlayground.tsx `farewell` WS frame handler
//     (Storm's tool-driven deterministic farewell — fires once before
//     `conversation_ended`).
//
// Strategy: try `decodeAudioData()` first so we transparently handle
// encoded formats (mp3 / opus / etc) the TTS backend might return; on
// decode failure fall back to raw 16-bit signed PCM at `sampleRate`
// (default 24 kHz — matches Gemini Live's voice output rate). Returns
// a Promise that resolves when playback finishes so callers can chain
// post-playback actions (e.g. teardown after farewell).
//
// Don't await this if you need the rest of the handler to run while
// audio is still playing — fire-and-forget is fine too, the AudioContext
// auto-closes 100ms after the source ends so there's no leak.

export async function playPcmAudio(
  base64Audio: string,
  sampleRate = 24000,
): Promise<void> {
  if (!base64Audio) return;

  const binaryStr = atob(base64Audio);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }

  const AudioContextClass =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const audioCtx = new AudioContextClass();

  const cleanup = () => {
    // Slight delay so the source has fully released its hold on the
    // context's destination before we close it (avoids tail-clipping
    // a frame on some Safari builds).
    setTimeout(() => {
      try {
        audioCtx.close();
      } catch {
        /* already closed */
      }
    }, 100);
  };

  try {
    // Encoded path (mp3/opus/etc). The buffer slice protects against
    // decodeAudioData detaching the underlying ArrayBuffer.
    const audioBuffer = await audioCtx.decodeAudioData(bytes.buffer.slice(0));
    await new Promise<void>((resolve) => {
      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtx.destination);
      source.onended = () => {
        cleanup();
        resolve();
      };
      source.start(0);
    });
  } catch {
    // Raw PCM16 fallback. Matches Storm's TTS default for the farewell
    // event (`sample_rate: 24000`).
    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768.0;
    }
    const audioBuffer = audioCtx.createBuffer(1, float32.length, sampleRate);
    audioBuffer.getChannelData(0).set(float32);
    await new Promise<void>((resolve) => {
      const source = audioCtx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioCtx.destination);
      source.onended = () => {
        cleanup();
        resolve();
      };
      source.start(0);
    });
  }
}
